import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import SEO from "@/components/SEO";
import { SERVICES, COMPANY } from "@/data/site";
import { SERVICE_CONTENT } from "@/data/serviceContent";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  ClipboardList,
  CalendarCheck,
  Truck,
  Home,
  Building2,
  MapPin,
  Sparkles,
  Sofa,
  PackageCheck,
  Briefcase,
  ShieldCheck,
  Star,
  Award,
} from "lucide-react";
import QuoteSection from "@/components/QuoteSection";

const ICONS = { Home, Building2, MapPin, Sparkles, Sofa, PackageCheck, Briefcase };
const PROCESS_ICONS = [ClipboardList, CalendarCheck, Truck];

const ServicePage = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const content = SERVICE_CONTENT[slug];

  if (!service || !content) return <Navigate to="/" replace />;

  const Icon = ICONS[service.icon] || Home;
  const related = (content.related || [])
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SEO
        title={content.seoTitle}
        description={content.metaDesc}
        path={`/services/${slug}`}
        extraSchema={faqSchema}
      />

      {/* 1. HERO */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="service-hero">
        <div className="absolute inset-0 -z-10">
          <img
            src={service.image}
            alt={`${content.h1} - Crosstown Movers`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[var(--ct-primary)]/85" />
        </div>
        <div className="section-container text-white">
          <Link to="/" className="text-sm text-white/70 hover:text-white">
            ← Back to Home
          </Link>
          <div className="mt-6 flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-[var(--ct-secondary)] text-[var(--ct-primary)] flex items-center justify-center">
              <Icon className="w-6 h-6" />
            </div>
            <div className="label-xs text-white">Crosstown Movers · Wyoming</div>
          </div>
          <h1
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl"
            style={{ fontFamily: "Outfit" }}
          >
            {content.h1}
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed">
            {content.subheadline}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#quote" className="btn-primary" data-testid="hero-quote">
              Get a Free Quote
            </a>
            <a
              href={`tel:${COMPANY.phoneTel}`}
              className="btn-outline !text-white !border-white hover:!bg-white hover:!text-[var(--ct-primary)]"
              data-testid="hero-call"
            >
              <Phone className="w-4 h-4" /> Call Now · {COMPANY.phone}
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-white/80">
            <Trust icon={<ShieldCheck className="w-4 h-4" />} text="Licensed & Insured" />
            <Trust icon={<Star className="w-4 h-4 fill-current" />} text="4.5★ · 60+ Reviews" />
            <Trust icon={<Award className="w-4 h-4" />} text="Wyoming Since 1980" />
          </div>
        </div>
      </section>

      {/* 2. INTRO */}
      <section className="py-16 md:py-20 bg-white" data-testid="service-intro">
        <div className="section-container max-w-4xl">
          <div className="label-xs text-[var(--ct-primary)] mb-3">About This Service</div>
          <h2
            className="text-3xl md:text-4xl font-semibold text-[var(--ct-primary)] tracking-tight leading-[1.15]"
            style={{ fontFamily: "Outfit" }}
          >
            Trusted by Wyoming families & businesses since 1980.
          </h2>
          <div className="mt-6 space-y-4 text-[var(--ct-text-muted)] leading-relaxed text-[17px]">
            {content.intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* 3. WHAT'S INCLUDED + 4. BENEFITS (two-col) */}
      <section className="py-16 md:py-20 bg-[var(--ct-bg)]" data-testid="service-includes">
        <div className="section-container grid lg:grid-cols-2 gap-12">
          <div>
            <div className="label-xs text-[var(--ct-primary)] mb-3">What's Included</div>
            <h2
              className="text-3xl font-semibold text-[var(--ct-primary)] tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              Everything you get with this service.
            </h2>
            <ul className="mt-6 space-y-3">
              {content.includes.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-[var(--ct-text)]">
                  <CheckCircle2 className="w-5 h-5 text-[var(--ct-secondary)] mt-1 flex-shrink-0" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="label-xs text-[var(--ct-primary)] mb-3">Benefits</div>
            <h2
              className="text-3xl font-semibold text-[var(--ct-primary)] tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              Why customers choose this service.
            </h2>
            <div className="mt-6 grid gap-3">
              {content.benefits.map((b, i) => (
                <div
                  key={i}
                  className="p-5 rounded-2xl bg-white border border-[var(--ct-border)] flex items-start gap-3"
                >
                  <span className="w-8 h-8 rounded-full bg-[var(--ct-primary)] text-[var(--ct-secondary)] flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-[var(--ct-text)] leading-relaxed">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5. WHY CHOOSE US */}
      <section className="py-16 md:py-20 bg-[var(--ct-primary)] text-white" data-testid="service-why">
        <div className="section-container">
          <div className="max-w-3xl">
            <div className="label-xs text-white mb-3">Why Crosstown Movers</div>
            <h2
              className="text-3xl md:text-4xl font-semibold tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              The Wyoming moving company customers come back to.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {content.whyUs.map((w, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur"
              >
                <CheckCircle2 className="w-5 h-5 text-[var(--ct-secondary)]" />
                <p className="mt-3 text-white/90 leading-relaxed">{w}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. HOW IT WORKS */}
      <section className="py-16 md:py-20 bg-white" data-testid="service-process">
        <div className="section-container">
          <div className="max-w-3xl">
            <div className="label-xs text-[var(--ct-primary)] mb-3">How It Works</div>
            <h2
              className="text-3xl md:text-4xl font-semibold text-[var(--ct-primary)] tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              Three simple steps to a stress-free move.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {content.process.map((step, i) => {
              const Icon = PROCESS_ICONS[i] || ClipboardList;
              return (
                <div
                  key={i}
                  className="p-7 rounded-2xl bg-[var(--ct-bg)] border border-[var(--ct-border)]"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[var(--ct-primary)] text-[var(--ct-secondary)] flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-xs font-bold tracking-[0.25em] uppercase text-[var(--ct-secondary)]">
                      Step {i + 1}
                    </div>
                  </div>
                  <h3
                    className="mt-4 text-xl font-semibold text-[var(--ct-primary)]"
                    style={{ fontFamily: "Outfit" }}
                  >
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm text-[var(--ct-text-muted)] leading-relaxed">
                    {step.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7. SERVICE AREAS */}
      <section className="py-16 md:py-20 bg-[var(--ct-bg)]" data-testid="service-areas">
        <div className="section-container">
          <div className="max-w-3xl">
            <div className="label-xs text-[var(--ct-primary)] mb-3">Service Areas</div>
            <h2
              className="text-3xl md:text-4xl font-semibold text-[var(--ct-primary)] tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              Wyoming-wide coverage. Local Wyoming movers.
            </h2>
            <p className="mt-4 text-[var(--ct-text-muted)] leading-relaxed">
              We provide moving services across all major Wyoming cities, including{" "}
              <strong>movers in Casper</strong>, Cheyenne, Laramie, and surrounding areas.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            {content.areas.map((area) => (
              <span
                key={area}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[var(--ct-border)] text-[var(--ct-primary)] font-semibold text-sm"
              >
                <MapPin className="w-4 h-4 text-[var(--ct-secondary)]" />
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 8. PRICING */}
      <section className="py-16 md:py-20 bg-white" data-testid="service-pricing">
        <div className="section-container grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="label-xs text-[var(--ct-primary)] mb-3">Pricing</div>
            <h2
              className="text-3xl md:text-4xl font-semibold text-[var(--ct-primary)] tracking-tight leading-[1.15]"
              style={{ fontFamily: "Outfit" }}
            >
              {content.pricing.title}
            </h2>
            <p className="mt-5 text-[var(--ct-text-muted)] leading-relaxed">
              {content.pricing.text}
            </p>
            <a href="#quote" className="btn-primary mt-7">
              Get Your Free Quote
            </a>
          </div>
          <div className="lg:col-span-7 grid gap-3">
            {content.pricing.bullets.map((b, i) => (
              <div
                key={i}
                className="p-5 rounded-2xl bg-[var(--ct-bg)] border border-[var(--ct-border)] flex items-start gap-3"
              >
                <CheckCircle2 className="w-5 h-5 text-[var(--ct-secondary)] mt-1 flex-shrink-0" />
                <span className="text-[var(--ct-text)] leading-relaxed">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-16 md:py-20 bg-[var(--ct-bg)]" data-testid="service-faq">
        <div className="section-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-xs text-[var(--ct-primary)] mb-3">FAQ</div>
            <h2
              className="text-3xl md:text-4xl font-semibold text-[var(--ct-primary)] tracking-tight leading-[1.15]"
              style={{ fontFamily: "Outfit" }}
            >
              Common questions, answered.
            </h2>
            <p className="mt-4 text-[var(--ct-text-muted)]">
              Don't see your question? Call{" "}
              <a
                href={`tel:${COMPANY.phoneTel}`}
                className="font-semibold text-[var(--ct-primary)] underline"
              >
                {COMPANY.phone}
              </a>{" "}
              — we're open 24 hours.
            </p>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {content.faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border-b border-[var(--ct-border)]"
                >
                  <AccordionTrigger
                    className="text-left font-semibold text-[var(--ct-primary)] text-base md:text-lg hover:no-underline"
                    style={{ fontFamily: "Outfit" }}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[var(--ct-text-muted)] leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Related services */}
      {related.length > 0 && (
        <section className="py-16 bg-white" data-testid="service-related">
          <div className="section-container">
            <div className="label-xs text-[var(--ct-primary)] mb-3">Related Services</div>
            <h2
              className="text-2xl md:text-3xl font-semibold text-[var(--ct-primary)] tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              Explore more from Crosstown Movers.
            </h2>
            <div className="mt-7 grid sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  className="p-6 rounded-2xl bg-[var(--ct-bg)] border border-[var(--ct-border)] hover:border-[var(--ct-primary)] hover:-translate-y-1 transition-all"
                >
                  <div className="font-semibold text-[var(--ct-primary)]">{r.title}</div>
                  <div className="text-xs text-[var(--ct-text-muted)] mt-1">{r.seoTitle}</div>
                  <div className="mt-4 text-sm font-semibold text-[var(--ct-secondary)] inline-flex items-center gap-1">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 10. FINAL CTA */}
      <section
        className="py-20 bg-[var(--ct-primary)] text-white relative overflow-hidden"
        data-testid="service-final-cta"
      >
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[var(--ct-secondary)]/10 blur-3xl pointer-events-none" />
        <div className="section-container relative text-center max-w-3xl">
          <div className="label-xs text-white mb-3 mx-auto justify-center inline-flex">
            Ready When You Are
          </div>
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Get your free quote today.
            <br />
            <span className="text-[var(--ct-secondary)]">Move with confidence.</span>
          </h2>
          <p className="mt-6 text-white/80 leading-relaxed">
            Trusted Wyoming movers since 1980 — licensed, insured, and rated 4.5 stars by
            Wyoming families and businesses. Call now or request a free quote.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <a href="#quote" className="btn-primary">
              Get Your Free Quote <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={`tel:${COMPANY.phoneTel}`}
              className="btn-outline !text-white !border-white hover:!bg-white hover:!text-[var(--ct-primary)]"
            >
              <Phone className="w-4 h-4" /> Call {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      <QuoteSection />
    </>
  );
};

const Trust = ({ icon, text }) => (
  <span className="inline-flex items-center gap-2">
    <span className="text-[var(--ct-secondary)]">{icon}</span>
    {text}
  </span>
);

export default ServicePage;
